import { IssueTemplateBuilderEditable } from '@/ui/DTO/issueTemplateBuilderEditable';
import { IssuePriority } from '@/entities/issue';
import { LegacyPhoto } from '@/ui/pages/issue/legacyTypes';

export interface IssueLegacy {
  node_id?: string;
  uuid: string;
  type: 'action';
  action: 'issue';
  visible_for_conductor: boolean;
  entity_item_specific_assignments: boolean;
  approvals_enabled: boolean;
  approvals_entity_specific_assignments: boolean;
  executed_at: string | null;
  photos?: Array<LegacyPhoto>;
  settings: {
    assignees_enabled: boolean;
    assignees_required: boolean;
    description_enabled: boolean;
    description_required: boolean;
    due_after_enabled: boolean;
    due_after_required: boolean;
    photos_enabled: boolean;
    photos_required: boolean;
    priority_enabled: boolean;
    priority_required: boolean;
    title_enabled: boolean;
    title_required: boolean;
  };
  params: {
    title: string;
    description: IssueTemplateBuilderEditable['description']['value'];
    due_in: IssueTemplateBuilderEditable['dueIn']['value'];
    priority: IssuePriority;
    assignees: {
      users: Array<string>;
      groups: Array<string>;
    };
    associateAssignees: Array<string>;
    approvers: {
      users: Array<string | number>;
    };
  };
}

export const emptyIssueLegacy = (): IssueLegacy => ({
  uuid: '',
  type: 'action',
  action: 'issue',
  visible_for_conductor: false,
  entity_item_specific_assignments: false,
  approvals_enabled: false,
  approvals_entity_specific_assignments: false,
  executed_at: null,
  photos: [],
  settings: {
    assignees_enabled: false,
    assignees_required: false,
    description_enabled: false,
    description_required: false,
    due_after_enabled: false,
    due_after_required: false,
    photos_enabled: false,
    photos_required: false,
    priority_enabled: false,
    priority_required: false,
    title_enabled: false,
    title_required: false,
  },
  params: {
    title: '',
    description: '',
    due_in: { weeks: null, days: null, hours: null, minutes: null },
    priority: IssuePriority.Low,
    associateAssignees: [],
    approvers: {
      users: [],
    },
    assignees: {
      users: [],
      groups: [],
    },
  },
});

export const jsonIssueToIssueTemplateEditableAdaptor = (issueLegacy: IssueLegacy): IssueTemplateBuilderEditable => {
  const hasSettings = Object.prototype.hasOwnProperty.call(issueLegacy, 'settings');

  return {
    title: {
      definedByInspector: hasSettings ? issueLegacy.settings.title_enabled : false,
      required: hasSettings ? issueLegacy.settings.title_required : false,
      value: issueLegacy.params.title,
    },
    description: {
      definedByInspector: hasSettings ? issueLegacy.settings.description_enabled : false,
      required: hasSettings ? issueLegacy.settings.description_required : false,
      value: issueLegacy.params.description,
    },
    assignees: {
      definedByInspector: hasSettings ? issueLegacy.settings.assignees_enabled : false,
      required: hasSettings ? issueLegacy.settings.assignees_required : false,
      value: issueLegacy.params.assignees.users?.map(user => `user_${user}`) || [],
    },
    associateAssignees: {
      definedByInspector: hasSettings ? issueLegacy.settings.assignees_enabled : false,
      required: hasSettings ? issueLegacy.settings.assignees_required : false,
      value: issueLegacy.params.associateAssignees || [],
    },
    assignedGroups: {
      definedByInspector: hasSettings ? issueLegacy.settings.assignees_enabled : false,
      required: hasSettings ? issueLegacy.settings.assignees_required : false,
      value: issueLegacy.params.assignees.groups?.map(group => `group_${group}`) || [],
    },
    priority: {
      definedByInspector: hasSettings ? issueLegacy.settings.priority_enabled : false,
      required: hasSettings ? issueLegacy.settings.priority_required : false,
      value: issueLegacy.params.priority,
    },
    dueIn: {
      definedByInspector: hasSettings ? issueLegacy.settings.due_after_enabled : false,
      required: hasSettings ? issueLegacy.settings.due_after_required : false,
      value: issueLegacy.params.due_in,
    },
    photos: {
      definedByInspector: hasSettings ? issueLegacy.settings.photos_enabled : false,
      required: hasSettings ? issueLegacy.settings.photos_required : false,
    },
    entityItemSpecificAssignments: issueLegacy.entity_item_specific_assignments,
    approvalsEnabled: issueLegacy.approvals_enabled,
    approvers: {
      definedByInspector: false,
      required: false,
      value: issueLegacy.params.approvers?.users || [],
    },
    approvalsEntitySpecificAssignments: issueLegacy.approvals_entity_specific_assignments,
    showIssueHasBeenCreated: issueLegacy.visible_for_conductor,
  };
};
